import React from 'react'
import { Input, Select } from '@surecompanies/core_components'
import { Drawer } from 'antd'

import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import {
  ExplicitCreditReason,
  PullDayFilter,
} from '../../../api/reconciliation-v2-enums'
import {
  EXPLICIT_CREDIT_REASON_LABELS,
  EXPLICIT_CREDIT_REASONS,
  PULL_DAY_FILTER_OPTIONS,
} from '../recon-v2-helpers'
import { buildPeriodKey, CREDIT_NOTE_MAX_LENGTH, MONTHS } from './helpers'
import { CheckSquare } from './pills'
import { ChevronLeftSvg, ChevronRightSvg } from './icons'
import styles from './FiltersDrawer.module.scss'

/**
 * Filters drawer for the BenefitsTable — company/carrier/empty-in-month/pull
 * date selects, year + range toggle, the discrepancy checkboxes, and the
 * explicit credit filter (has/no credit, reasons, note text). Filters apply
 * live as they change; the footer's Apply just dismisses the panel. All state
 * lives in the parent; this component is a pure controlled view.
 */

interface FiltersDrawerProps {
  open: boolean
  onClose: () => void
  clients: SimpleCustomerDto[]
  selectedClientId: string | undefined
  onClientChange: (v: string | undefined) => void
  carrierFilter: string | undefined
  carrierOptions: { label: string; value: string }[]
  onCarrierChange: (v: string | undefined) => void
  emptyInMonth: string | undefined
  onEmptyInMonthChange: (v: string | undefined) => void
  year: number
  onYearChange: (updater: (y: number) => number) => void
  showFullYear: boolean
  onToggleFullYear: () => void
  hasIssueFilter: boolean | undefined
  onToggleHasIssue: () => void
  pullDays: PullDayFilter[]
  onPullDaysChange: (v: PullDayFilter[]) => void
  /** undefined = any; false = no explicit credit. The inputs below show only for true. */
  hasExplicitCredit: boolean | undefined
  onHasExplicitCreditChange: (v: boolean | undefined) => void
  creditReasons: ExplicitCreditReason[]
  onCreditReasonsChange: (v: ExplicitCreditReason[]) => void
  /** Typed (not yet applied) note text; the parent debounces it. */
  creditNote: string
  onCreditNoteChange: (v: string) => void
  onClearAll: () => void
}

const CREDIT_PRESENCE_OPTIONS = [
  { value: 'yes' as const, label: 'Has explicit credit' },
  { value: 'no' as const, label: 'No explicit credit' },
]

// Matching is by substring, term by term — abbreviations don't match.
const CREDIT_NOTE_HINT =
  'Search a word that appears in the note. Every word must appear in the same note.'

const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  open,
  onClose,
  clients,
  selectedClientId,
  onClientChange,
  carrierFilter,
  carrierOptions,
  onCarrierChange,
  emptyInMonth,
  onEmptyInMonthChange,
  year,
  onYearChange,
  showFullYear,
  onToggleFullYear,
  hasIssueFilter,
  onToggleHasIssue,
  pullDays,
  onPullDaysChange,
  hasExplicitCredit,
  onHasExplicitCreditChange,
  creditReasons,
  onCreditReasonsChange,
  creditNote,
  onCreditNoteChange,
  onClearAll,
}) => (
  <Drawer
    title={<span className={styles.title}>Filters</span>}
    placement="right"
    width={360}
    open={open}
    onClose={onClose}
    footer={
      <div className={styles.footer}>
        <button type="button" className={styles.ghostBtn} onClick={onClearAll}>
          Clear all
        </button>
        {/* Filters apply live as they change; Apply dismisses the panel. */}
        <button type="button" className={styles.tealBtn} onClick={onClose}>
          Apply filters
        </button>
      </div>
    }
  >
    <div className={styles.body}>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Company</div>
        <Select
          placeholder="All companies"
          value={selectedClientId}
          onChange={onClientChange}
          style={{ width: '100%' }}
          size="large"
          allowClear
          showSearch
          optionFilterProp="label"
          options={clients.map(({ name, id }) => ({
            value: id,
            label: name,
          }))}
        />
      </div>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Carrier</div>
        <Select
          placeholder="All carriers"
          value={carrierFilter}
          onChange={onCarrierChange}
          style={{ width: '100%' }}
          size="large"
          allowClear
          showSearch
          optionFilterProp="label"
          options={carrierOptions}
        />
      </div>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Empty in month</div>
        <Select
          placeholder="Empty in month…"
          value={emptyInMonth}
          onChange={onEmptyInMonthChange}
          style={{ width: '100%' }}
          size="large"
          allowClear
          options={MONTHS.map((m, idx) => ({
            label: m,
            value: buildPeriodKey(year, idx),
          }))}
        />
      </div>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Pull date</div>
        <Select<PullDayFilter[]>
          mode="multiple"
          placeholder="Any pull date"
          value={pullDays}
          onChange={onPullDaysChange}
          style={{ width: '100%' }}
          size="large"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          options={PULL_DAY_FILTER_OPTIONS}
        />
      </div>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Date range</div>
        <div className={styles.rangeRow}>
          <div className={styles.yearBox}>
            <button
              type="button"
              className={styles.yearChevBtn}
              aria-label="Previous year"
              onClick={() => onYearChange((y) => y - 1)}
            >
              <ChevronLeftSvg size={15} />
            </button>
            <span>{year}</span>
            <button
              type="button"
              className={styles.yearChevBtn}
              aria-label="Next year"
              onClick={() => onYearChange((y) => y + 1)}
            >
              <ChevronRightSvg size={15} />
            </button>
          </div>
          <button
            type="button"
            className={`${styles.rangeToggleBtn}${
              !showFullYear ? ` ${styles.active}` : ''
            }`}
            title={
              showFullYear
                ? 'Showing all 12 months — click to show months up to today'
                : 'Showing months up to today — click to show all 12 months'
            }
            onClick={onToggleFullYear}
          >
            {showFullYear ? 'Full year' : 'Up to today'}
          </button>
        </div>
      </div>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Discrepancy</div>
        <div className={styles.checkStack}>
          <button
            type="button"
            className={styles.checkRow}
            onClick={onToggleHasIssue}
          >
            <CheckSquare on={hasIssueFilter === true}>
              {hasIssueFilter === true ? '✓' : ''}
            </CheckSquare>
            Show only benefits with errors
          </button>
        </div>
      </div>
      <div className={styles.field}>
        <div className={styles.fieldOverline}>Explicit credit</div>
        <Select<'yes' | 'no'>
          aria-label="Has explicit credit"
          placeholder="Any"
          value={
            hasExplicitCredit === undefined
              ? undefined
              : hasExplicitCredit
                ? 'yes'
                : 'no'
          }
          onChange={(v) =>
            onHasExplicitCreditChange(v === undefined ? undefined : v === 'yes')
          }
          style={{ width: '100%' }}
          size="large"
          allowClear
          options={CREDIT_PRESENCE_OPTIONS}
        />
      </div>
      {/* Reason and note narrow "has explicit credit", so they show only under it. */}
      {hasExplicitCredit === true && (
        <>
          <div className={styles.field}>
            <div className={styles.fieldOverline}>Credit reason</div>
            <Select<ExplicitCreditReason[]>
              aria-label="Credit reason"
              mode="multiple"
              placeholder="Any reason"
              value={creditReasons}
              onChange={onCreditReasonsChange}
              style={{ width: '100%' }}
              size="large"
              allowClear
              maxTagCount="responsive"
              options={EXPLICIT_CREDIT_REASONS.map((r) => ({
                value: r,
                label: EXPLICIT_CREDIT_REASON_LABELS[r],
              }))}
            />
          </div>
          <div className={styles.field}>
            <div className={styles.fieldOverline}>Credit note contains</div>
            <Input
              aria-label="Credit note contains"
              placeholder='e.g. "reward"'
              value={creditNote}
              onChange={(e) => onCreditNoteChange(e.target.value)}
              maxLength={CREDIT_NOTE_MAX_LENGTH}
              size="large"
              allowClear
            />
            <div className={styles.fieldHint}>{CREDIT_NOTE_HINT}</div>
          </div>
        </>
      )}
    </div>
  </Drawer>
)

export default FiltersDrawer
